import axiosInstance from './axiosInstance';

export interface AnalisisItem {
  id: string;
  fecha: string;
  createdAt: string;
  observacion?: string;
  medicoSolicitante?: string;
  estudiosResumen: string;
  tienePdf: boolean;
  pacienteNombre: string;
  pacienteApellido: string;
  pacienteDni: string;
}

export interface LaboratorioOption {
  id: number;
  nombre: string;
}

export interface EstudioOption {
  id: number;
  codigo: string;
  descripcion: string;
  unidadMedida: string | null;
  frecuente: boolean;
  metodo: string | null;
  formatoPdf: string | null;
  criterio: string | null;
  minimo: number | null;
  maximo: number | null;
  laboratorioId: number | null;
  laboratorioNombre: string | null;
}

export interface AnalisisCreateRequest {
  pacienteId: string;
  fecha: string;
  observacion: string;
  medicoSolicitante: string;
  items: { estudioId: number; resultado: string; laboratorioId: number }[];
}

export async function buscarAnalisis(q: string): Promise<AnalisisItem[]> {
  const response = await axiosInstance.get<AnalisisItem[]>('/api/analisis/buscar', { params: { q } });
  return response.data;
}

export interface GrupoResultados {
  pacienteNombre: string;
  pacienteApellido: string;
  pacienteDni: string;
  esPropio: boolean;
  resultados: AnalisisItem[];
}

export async function misResultados(desde?: string, hasta?: string): Promise<GrupoResultados[]> {
  const response = await axiosInstance.get<GrupoResultados[]>('/api/analisis/mis-resultados', {
    params: { desde: desde || undefined, hasta: hasta || undefined },
  });
  return response.data;
}

export async function crearAnalisis(data: AnalisisCreateRequest): Promise<AnalisisItem> {
  const response = await axiosInstance.post<AnalisisItem>('/api/analisis', data);
  return response.data;
}

export async function getLaboratorios(): Promise<LaboratorioOption[]> {
  const response = await axiosInstance.get<LaboratorioOption[]>('/api/laboratorios');
  return response.data;
}

export async function getEstudios(): Promise<EstudioOption[]> {
  const response = await axiosInstance.get<EstudioOption[]>('/api/estudios');
  return response.data;
}

export interface EstudioRequest {
  codigo: string;
  descripcion: string;
  unidadMedida: string;
  frecuente: boolean;
  metodo: string;
  formatoPdf: string;
  criterio: string;
  minimo: number | null;
  maximo: number | null;
  laboratorioId: number | null;
}

export interface LaboratorioRequest {
  nombre: string;
}

export async function crearEstudio(data: EstudioRequest): Promise<EstudioOption> {
  const response = await axiosInstance.post<EstudioOption>('/api/estudios', data);
  return response.data;
}

export async function actualizarEstudio(id: number, data: EstudioRequest): Promise<EstudioOption> {
  const response = await axiosInstance.put<EstudioOption>(`/api/estudios/${id}`, data);
  return response.data;
}

export async function crearLaboratorio(data: LaboratorioRequest): Promise<LaboratorioOption> {
  const response = await axiosInstance.post<LaboratorioOption>('/api/laboratorios', data);
  return response.data;
}

export async function actualizarLaboratorio(id: number, data: LaboratorioRequest): Promise<LaboratorioOption> {
  const response = await axiosInstance.put<LaboratorioOption>(`/api/laboratorios/${id}`, data);
  return response.data;
}

export async function descargarPdf(analisisId: string, nombreArchivo: string): Promise<void> {
  const response = await axiosInstance.get(`/api/analisis/${analisisId}/pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombreArchivo;
  link.click();
  URL.revokeObjectURL(url);
}

export async function verPdf(analisisId: string): Promise<void> {
  const response = await axiosInstance.get(`/api/analisis/${analisisId}/pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(response.data as Blob);
  window.open(url, '_blank');
}

export interface EmailSugeridoResponse {
  email: string | null;
  encontrado: boolean;
}

export interface MessageResponse {
  codigo: string;
  mensaje: string;
  timestamp: string;
}

export interface ActualizarAnalisisRequest {
  fecha: string;
  observacion: string;
  medicoSolicitante: string;
  items: { estudioId: number; resultado: string; laboratorioId: number }[];
}

export interface AnalisisItemDetail {
  id: string;
  estudioId: number;
  estudioCodigo: string;
  estudioDescripcion: string;
  unidadMedida: string | null;
  resultado: string | null;
  laboratorioId: number;
  laboratorioNombre: string;
}

export interface AnalisisDetail extends AnalisisItem {
  items: AnalisisItemDetail[];
}

export async function getAnalisis(id: string): Promise<AnalisisDetail> {
  const response = await axiosInstance.get<AnalisisDetail>(`/api/analisis/${id}`);
  return response.data;
}

export async function actualizarAnalisis(id: string, data: ActualizarAnalisisRequest): Promise<AnalisisItem> {
  const response = await axiosInstance.put<AnalisisItem>(`/api/analisis/${id}`, data);
  return response.data;
}

export async function eliminarAnalisis(id: string): Promise<void> {
  await axiosInstance.delete(`/api/analisis/${id}`);
}

export async function getEmailSugerido(analisisId: string): Promise<EmailSugeridoResponse> {
  const response = await axiosInstance.get<EmailSugeridoResponse>(`/api/analisis/${analisisId}/email-sugerido`);
  return response.data;
}

export async function enviarEmail(analisisId: string, email: string): Promise<MessageResponse> {
  const response = await axiosInstance.post<MessageResponse>(`/api/analisis/${analisisId}/enviar-email`, { email });
  return response.data;
}
